import type { Workflow, Participant, WorkflowBinding } from './types'

// Twasta ExtendedAttribute names — EXACT parity with the Python engine
// (packages/runtime-engine/twasta_runtime_engine/workflow/xpdl.py).
const ATTR_PROFILE = 'TWASTA_PROFILE'
const ATTR_BIND_OBJECT = 'TWASTA_BIND_OBJECT'
const ATTR_BIND_TRIGGER = 'TWASTA_BIND_TRIGGER'
const ATTR_END = 'TWASTA_END'
const ATTR_ON = 'TWASTA_ON'
const ATTR_USERS = 'TWASTA_USERS'

function esc(str: string | undefined | null): string {
  if (!str) return ''
  return str.replace(/[&<>"']/g, c => ({
    '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&apos;',
  })[c]!)
}

/** Generate XPDL 1.0 XML from workflow state — matches legacy format, plus the
 *  Twasta approval ExtendedAttributes (TWASTA_*) the execution engine reads.
 *
 *  When `binding` is omitted (or empty) NO process-level TWASTA_* markers are
 *  emitted, so a plain non-approval workflow serialises exactly as before. */
export function generateXpdl(
  objectName: string,
  workflow: Workflow,
  participants: Participant[],
  binding?: WorkflowBinding,
): string {
  const date = new Date().toISOString().split('T')[0]

  return `<?xml version="1.0" encoding="UTF-8"?>
<Package Id="${esc(objectName)}" Name="${esc(objectName)}" xmlns="http://www.wfmc.org/2002/XPDL1.0" xmlns:xpdl="http://www.wfmc.org/2002/XPDL1.0" xmlns:xsi="http://www.w3.org/2001/XMLSchema-instance">
    <PackageHeader>
        <XPDLVersion>1.0</XPDLVersion>
        <Vendor>Workflow Designer</Vendor>
        <Created>${date}</Created>
    </PackageHeader>
    <RedefinableHeader PublicationStatus="UNDER_TEST"/>
    <ConformanceClass GraphConformance="NON_BLOCKED"/>
    <WorkflowProcesses>
        <WorkflowProcess AccessLevel="PUBLIC" Id="${esc(objectName)}" Name="Process">
            <ProcessHeader DurationUnit="D">
                <Created>${date}</Created>
            </ProcessHeader>
            <RedefinableHeader PublicationStatus="UNDER_TEST"/>
            <Participants>
${genParticipants(participants)}
            </Participants>
            <Applications>
${genApplications(workflow)}
            </Applications>
            <Activities>
${genActivities(workflow)}
            </Activities>
            <Transitions>
${genTransitions(workflow)}
            </Transitions>
            <ExtendedAttributes>
${genProcessExt(workflow, binding)}
            </ExtendedAttributes>
        </WorkflowProcess>
    </WorkflowProcesses>
</Package>`
}

function genParticipants(participants: Participant[]): string {
  return participants.map(p => {
    let roleAttrs = ''
    if (p.type === 'ROLE') {
      const extAttrs: string[] = []
      if (p.roleCode !== undefined)
        extAttrs.push(`                    <ExtendedAttribute Name="PRFMR_ROLE_CODE" Value="${esc(p.roleCode)}"/>`)
      if (p.participatesFor !== undefined)
        extAttrs.push(`                    <ExtendedAttribute Name="SIGN_FOR" Value="${esc(p.participatesFor)}"/>`)
      if (p.roleExpression)
        extAttrs.push(`                    <ExtendedAttribute Name="PRFMR_ROLE_EXPR" Value="${esc(p.roleExpression)}"/>`)
      if (p.roleType)
        extAttrs.push(`                    <ExtendedAttribute Name="PRFMR_ROLE_EXPR_TYPE" Value="${esc(p.roleType)}"/>`)
      if (p.users && p.users.length > 0)
        extAttrs.push(`                    <ExtendedAttribute Name="${ATTR_USERS}" Value="${esc(p.users.join(','))}"/>`)
      if (extAttrs.length > 0) {
        roleAttrs = `
                <ExtendedAttributes>
${extAttrs.join('\n')}
                </ExtendedAttributes>`
      }
    }

    return `            <Participant Id="${esc(p.id)}" Name="${esc(p.name)}">
                <ParticipantType Type="${esc(p.type)}"/>${roleAttrs}
            </Participant>`
  }).join('\n')
}

function genApplications(workflow: Workflow): string {
  const appIds = new Set<string>()
  workflow.activities.forEach(a => {
    if (a.tool?.name) appIds.add(a.tool.name)
  })

  return Array.from(appIds).map(appId => {
    const activity = workflow.activities.find(a => a.tool?.name === appId)
    const toolType = activity?.tool?.type || 'Application'
    const formalParams = activity?.tool?.parameters?.map((p, i) =>
      `                        <FormalParameter Id="${esc(p.id)}" Index="${i + 1}" Mode="${esc(p.mode || 'IN')}">
                            <DataType>
                                <BasicType Type="${esc(p.dataType || 'STRING')}"/>
                            </DataType>
                        </FormalParameter>`
    ).join('\n') || ''

    const iconAttr = activity?.icon
      ? `\n                        <ExtendedAttribute Name="APPLN_ICON" Value="${esc(activity.icon)}"/>`
      : ''

    return `                <Application Id="${esc(appId)}" Name="${esc(appId)}">
                    <Description>${esc(appId)}</Description>
                    <FormalParameters>
${formalParams}
                    </FormalParameters>
                    <ExtendedAttributes>
                        <ExtendedAttribute Name="APPLN_TYPE" Value="${esc(toolType)}"/>${iconAttr}
                    </ExtendedAttributes>
                </Application>`
  }).join('\n')
}

function genActivities(workflow: Workflow): string {
  return workflow.activities.map(a => {
    let impl = ''
    if (a.type === 'activity' && a.tool?.name) {
      const actualParams = a.tool.parameters?.map(p =>
        `                            <ActualParameter>${esc(p.value)}</ActualParameter>`
      ).join('\n') || ''

      impl = `
                    <Implementation>
                        <Tool Id="${esc(a.tool.name)}">
${actualParams}
                        </Tool>
                    </Implementation>`
    }

    const performer = a.performer
      ? `\n                    <Performer>${esc(a.performer)}</Performer>`
      : ''

    let event = ''
    if (a.type === 'start') {
      event = `
                    <Event>
                        <StartEvent Trigger="None"/>
                    </Event>`
    } else if (a.type === 'end') {
      event = `
                    <Event>
                        <EndEvent Result="None"/>
                    </Event>`
    }

    const iconAttr = a.icon
      ? `\n                        <ExtendedAttribute Name="ActivityIcon" Value="${esc(a.icon)}"/>`
      : ''

    // Approval end-kind marker (only on end nodes that carry one).
    const endAttr = (a.type === 'end' && a.endKind)
      ? `\n                        <ExtendedAttribute Name="${ATTR_END}" Value="${esc(a.endKind)}"/>`
      : ''

    return `                <Activity Id="${esc(a.id)}" Name="${esc(a.description)}">${impl}${performer}${event}
                    <ExtendedAttributes>
                        <ExtendedAttribute Name="NodeGraphicsInfo" XCoordinate="${Math.round(a.x)}" YCoordinate="${Math.round(a.y)}"/>${iconAttr}${endAttr}
                    </ExtendedAttributes>
                </Activity>`
  }).join('\n')
}

function genTransitions(workflow: Workflow): string {
  return workflow.transitions.map(t => {
    // Condition: a non-empty expression wins (Type defaults to CONDITION);
    // an explicit OTHERWISE with no expression emits the empty unconditional
    // edge; otherwise (legacy) no Condition element at all.
    let cond = ''
    if (t.condition) {
      const ctype = t.conditionType === 'OTHERWISE' ? 'CONDITION' : (t.conditionType || 'CONDITION')
      cond = `\n                    <Condition Type="${esc(ctype)}">${esc(t.condition)}</Condition>`
    } else if (t.conditionType === 'OTHERWISE') {
      cond = '\n                    <Condition Type="OTHERWISE"/>'
    }

    // Decision channel: only emit TWASTA_ON when it diverges from the default
    // 'approve', so plain workflows stay byte-identical to the legacy output.
    let ext = ''
    if (t.on === 'reject') {
      ext = `
                    <ExtendedAttributes>
                        <ExtendedAttribute Name="${ATTR_ON}" Value="reject"/>
                    </ExtendedAttributes>`
    }

    return `                <Transition Id="${esc(t.id)}" From="${esc(t.from)}" To="${esc(t.to)}" Name="${esc(t.description || '')}">${cond}${ext}
                </Transition>`
  }).join('\n')
}

/** Process-level ExtendedAttributes: the Twasta binding markers (only when a
 *  binding is set) followed by the ToolId-keyed layout rows. */
function genProcessExt(workflow: Workflow, binding?: WorkflowBinding): string {
  const lines: string[] = []

  // Binding markers — omitted entirely for a plain (unbound) workflow so the
  // legacy serialisation is preserved.
  if (binding) {
    if (binding.profile)
      lines.push(`                <ExtendedAttribute Name="${ATTR_PROFILE}" Value="${esc(binding.profile)}"/>`)
    if (binding.bindObject)
      lines.push(`                <ExtendedAttribute Name="${ATTR_BIND_OBJECT}" Value="${esc(binding.bindObject)}"/>`)
    if (binding.bindTriggers && binding.bindTriggers.length > 0)
      lines.push(`                <ExtendedAttribute Name="${ATTR_BIND_TRIGGER}" Value="${esc(binding.bindTriggers.map(t => t.trim().toLowerCase()).filter(Boolean).join(','))}"/>`)
  }

  // Layout (ToolId-keyed) so the canvas restores positions.
  for (const a of workflow.activities) {
    lines.push(`                <ExtendedAttribute Name="NodeGraphicsInfo" ToolId="${esc(a.id)}" XCoordinate="${Math.round(a.x)}" YCoordinate="${Math.round(a.y)}" Width="${Math.round(a.width)}" Height="${Math.round(a.height)}"/>`)
  }

  return lines.join('\n')
}
